import {
  LogoutOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { history, useIntl, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { removeToken } from '@/utils/auth';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const intl = useIntl();
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return (
    <span className="anticon">
      {currentUser?.nickname || currentUser?.username || intl.formatMessage({ id: '顶栏.用户' })}
    </span>
  );
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu: _menu,
  children,
}) => {
  const intl = useIntl();
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    // 清除 token
    removeToken();
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === '_roles') return;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({
          ...s,
          currentUser: undefined,
          authz: undefined,
        }));
      });
      loginOut();
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser, authz } = initialState;

  if (!currentUser || !currentUser.username) {
    return loading;
  }

  const roleLabels =
    authz?.roles?.length ?
      authz.roles.map((r) => r.name || r.code).join('、')
    : null;
  const roleLabel = intl.formatMessage({ id: '顶栏.角色' });

  const menuItems: MenuProps['items'] = [
    ...(roleLabels
      ? [
          {
            key: '_roles',
            label: `${roleLabel}：${roleLabels}`,
            disabled: true,
            style: { cursor: 'default', color: 'rgba(0,0,0,0.45)' },
          },
          { type: 'divider' as const },
        ]
      : []),
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: intl.formatMessage({ id: '顶栏.个人设置' }),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: intl.formatMessage({ id: '顶栏.退出登录' }),
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
